import type { ComposedVerticals, VerticalModule } from './types'

/**
 * Merge the vertical registry into the shell's route + nav lists, failing
 * LOUDLY on any collision (UID-9). This is the client-side mirror of UID-7's
 * `build-contract.mjs` path-merge guard: a genuine collision is caught at
 * load time with a message naming both offenders — never a silent shadow.
 *
 * Guards, in order (each throws with both offending module ids/paths):
 *  1. basePaths must be pairwise DISJOINT — no exact dupe, and no one prefix
 *     of another (so `/rfq` and `/rfq/detail` can't both be owned).
 *  2. every route path must live within its own module's basePath (a
 *     vertical cannot mount a screen outside the prefix it owns).
 *  3. no two modules may register the same route path.
 *
 * Pure and total (no React, no registry import) so it is unit-testable in
 * isolation; ./index.ts applies it once to the real registry at module load.
 */
export function composeVerticals(modules: readonly VerticalModule[]): ComposedVerticals {
  assertDisjointBasePaths(modules)

  const seenPaths = new Map<string, string>() // path -> owning module id

  for (const mod of modules) {
    const bases = basePathsOf(mod)
    for (const route of mod.routes) {
      if (!bases.some((base) => isWithinBase(route.path, base))) {
        throw new Error(
          `Vertical "${mod.id}" registers route "${route.path}" outside its basePath(s) "${bases.join(', ')}". A vertical may only mount routes within a prefix it owns.`,
        )
      }
      const existingOwner = seenPaths.get(route.path)
      if (existingOwner) {
        throw new Error(
          `Duplicate vertical route "${route.path}" registered by both "${existingOwner}" and "${mod.id}". Route paths must be unique across verticals.`,
        )
      }
      seenPaths.set(route.path, mod.id)
    }
  }

  return {
    routes: modules.flatMap((mod) => mod.routes),
    navItems: modules.flatMap((mod) => mod.navItems ?? []),
  }
}

function assertDisjointBasePaths(modules: readonly VerticalModule[]): void {
  for (let i = 0; i < modules.length; i++) {
    const a = modules[i]
    if (!a) {
      continue
    }
    for (let j = i + 1; j < modules.length; j++) {
      const b = modules[j]
      if (!b) {
        continue
      }
      const aBases = basePathsOf(a)
      const bBases = basePathsOf(b)
      for (const aBase of aBases) {
        for (const bBase of bBases) {
          if (basePathsOverlap(aBase, bBase)) {
            throw new Error(
              `Vertical basePaths overlap: "${a.id}" owns "${aBase}" and "${b.id}" owns "${bBase}". Each vertical must own disjoint path prefixes (UID-9 disjoint-ownership; UID-70 multi-basePath).`,
            )
          }
        }
      }
    }
  }
}

/** Normalize a module's basePath (single string or list) to a list (UID-70). */
function basePathsOf(mod: VerticalModule): readonly string[] {
  return typeof mod.basePath === 'string' ? [mod.basePath] : mod.basePath
}

/** True when the two prefixes are equal or one is a path-segment prefix of the other. */
function basePathsOverlap(a: string, b: string): boolean {
  return a === b || isWithinBase(a, b) || isWithinBase(b, a)
}

/** True when `path` is `base` itself or a nested segment under it. */
function isWithinBase(path: string, base: string): boolean {
  return path === base || path.startsWith(`${base}/`)
}
